const CART_ADDRESS_COPY_FORM_SELECTOR = '[data-vm-cart-address-copy-form="shipping-calculator"]';
const CART_ADDRESS_COPY_LINK_SELECTOR = '[data-vm-cart-address-copy-link="shipping-calculator"]';
const CART_ADDRESS_COPY_SPINNER_SELECTOR = '[data-vm-cart-address-copy-spinner="shipping-calculator"]';
const CART_ADDRESS_COPY_FEEDBACK_SELECTOR = '[data-vm-cart-address-copy-feedback="shipping-calculator"]';

class VmCartAddressCopy {
    public constructor() {
        this.bind();
    }

    private bind(): void {
        document.addEventListener('click', async (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }

            const copyLink = target.closest<HTMLAnchorElement>(CART_ADDRESS_COPY_LINK_SELECTOR);
            if (!copyLink) {
                return;
            }

            event.preventDefault();

            if (copyLink.getAttribute('aria-disabled') === 'true') {
                return;
            }

            const form = copyLink.closest<HTMLFormElement>(CART_ADDRESS_COPY_FORM_SELECTOR);
            if (!form) {
                return;
            }

            const spinner = form.querySelector<HTMLElement>(CART_ADDRESS_COPY_SPINNER_SELECTOR);
            const feedback = form.querySelector<HTMLElement>(CART_ADDRESS_COPY_FEEDBACK_SELECTOR);
            const previousValues = this.getCurrentFieldValues(form);

            this.setCopyLinkLoadingState(copyLink, true);
            this.setLoadingState(spinner, true);
            this.setFieldDisabledState(form, true);

            try {
                const values = await this.fetchBillingValues(copyLink);
                this.copyBillingValuesIntoShippingForm(form, values);
                this.setFeedback(feedback, '');
            } catch (error) {
                this.restoreFieldValues(form, previousValues);
                this.setFeedback(feedback, error instanceof Error ? error.message : 'Cart address copy request failed.');
            } finally {
                this.setFieldDisabledState(form, false);
                this.setLoadingState(spinner, false);
                this.setCopyLinkLoadingState(copyLink, false);
            }
        });
    }

    private async fetchBillingValues(copyLink: HTMLAnchorElement): Promise<Record<string, string>> {
        const url = copyLink.dataset.vmCartAddressCopyUrl;
        const action = copyLink.dataset.vmCartAddressCopyAction;
        const nonceName = copyLink.dataset.vmCartAddressCopyNonceName;
        const nonceValue = copyLink.dataset.vmCartAddressCopyNonceValue;

        if (!url || !action || !nonceName || !nonceValue) {
            throw new Error('Cart address copy endpoint is not configured.');
        }

        const payload = new URLSearchParams();
        payload.set('action', action);
        payload.set(nonceName, nonceValue);

        const response = await fetch(url, {
            body: payload,
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            method: 'POST',
        });

        const result = (await response.json()) as {
            success?: boolean;
            data?: {
                fields?: Record<string, string>;
                message?: string;
            };
        };

        if (!response.ok || !result?.success || !result.data?.fields) {
            throw new Error(result?.data?.message || 'Cart address copy request failed.');
        }

        return result.data.fields;
    }

    private copyBillingValuesIntoShippingForm(form: HTMLFormElement, values: Record<string, string>): void {
        const countryFieldName = 'calc_shipping_country';
        const stateFieldName = 'calc_shipping_state';

        if (countryFieldName in values) {
            this.applyFieldValue(form, countryFieldName, values[countryFieldName]);
        }

        window.requestAnimationFrame(() => {
            Object.entries(values).forEach(([fieldName, fieldValue]) => {
                if (fieldName === countryFieldName || fieldName === stateFieldName) {
                    return;
                }

                this.applyFieldValue(form, fieldName, fieldValue);
            });

            window.requestAnimationFrame(() => {
                if (stateFieldName in values) {
                    this.applyFieldValue(form, stateFieldName, values[stateFieldName]);
                }

                this.refreshSelectUi(form);
            });
        });
    }

    private isShippingCalculatorField(target: EventTarget | null): target is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
        if (
            !(
                target instanceof HTMLInputElement ||
                target instanceof HTMLSelectElement ||
                target instanceof HTMLTextAreaElement
            )
        ) {
            return false;
        }

        return target.name.startsWith('calc_shipping_');
    }

    private applyFieldValue(form: HTMLFormElement, fieldName: string, fieldValue: string): void {
        const field = form.elements.namedItem(fieldName);
        if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) {
            return;
        }

        field.value = fieldValue;
        field.dispatchEvent(new Event('change', { bubbles: true }));
        field.dispatchEvent(new Event('input', { bubbles: true }));

        if (typeof jQuery !== 'undefined') {
            const $field = jQuery(field);
            $field.trigger('change');
            $field.trigger('change.select2');
        }
    }

    private getCurrentFieldValues(form: HTMLFormElement): Record<string, string> {
        const values: Record<string, string> = {};

        Array.from(form.elements).forEach((element) => {
            if (!this.isShippingCalculatorField(element)) {
                return;
            }

            values[element.name] = element.value;
        });

        return values;
    }

    private restoreFieldValues(form: HTMLFormElement, values: Record<string, string>): void {
        Object.entries(values).forEach(([fieldName, fieldValue]) => {
            this.applyFieldValue(form, fieldName, fieldValue);
        });
    }

    private setFieldDisabledState(form: HTMLFormElement, isDisabled: boolean): void {
        Array.from(form.elements).forEach((element) => {
            if (!this.isShippingCalculatorField(element)) {
                return;
            }

            element.disabled = isDisabled;
        });
    }

    private setCopyLinkLoadingState(copyLink: HTMLAnchorElement, isLoading: boolean): void {
        copyLink.classList.toggle('is-loading', isLoading);
        copyLink.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        copyLink.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
        copyLink.tabIndex = isLoading ? -1 : 0;
    }

    private setLoadingState(spinner: HTMLElement | null, isLoading: boolean): void {
        if (!spinner) {
            return;
        }

        spinner.classList.toggle('is-visible', isLoading);
        spinner.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
    }

    /**
     * Announce the latest copy request result to assistive technology.
     *
     * @param feedback Live feedback element.
     * @param message Message to announce.
     */
    private setFeedback(feedback: HTMLElement | null, message: string): void {
        if (feedback) {
            feedback.textContent = message;
        }
    }

    private refreshSelectUi(form: HTMLFormElement): void {
        if (typeof jQuery === 'undefined' || typeof (jQuery.fn as any).selectWoo !== 'function') {
            return;
        }

        const $form = jQuery(form);

        window.setTimeout(() => {
            $form.find('select.country_select, select.state_select').each(function () {
                const $select = jQuery(this);
                const selectWoo = ($select as any).selectWoo?.bind($select);

                if ($select.hasClass('select2-hidden-accessible') && selectWoo) {
                    try {
                        selectWoo('destroy');
                    } catch (_error) {
                        // Ignore teardown races from WooCommerce state refresh.
                    }
                }

                $select
                    .closest('.woocommerce-input-wrapper, .form-row, .woocommerce-form-row')
                    .find('.select2-container')
                    .remove();

                if (selectWoo) {
                    selectWoo({
                        placeholder: $select.attr('data-placeholder') || $select.attr('placeholder') || '',
                        width: '100%',
                    });
                }

                $select.trigger('change.select2');
            });
        }, 0);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new VmCartAddressCopy();
});
